import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, CreatorName, Loading } from "../components/common";
import { toast } from "sonner";
import { Trophy, Crown, Check, Sparkles, Star, Globe, Clock, Vote, Heart, History } from "lucide-react";

const PHASE_LABEL = {
  qualification: "Qualification Round",
  top10_voting: "Top 10 — Public Voting Live",
  finalist_voting: "Top 5 Finalists — Voting Live",
  reveal: "Winners Revealed",
  archived: "Archived",
};

function countdown(showDate) {
  const d = new Date(showDate + "T20:00:00Z") - new Date();
  if (d <= 0) return null;
  const days = Math.floor(d / 86400000);
  const hrs = Math.floor((d % 86400000) / 3600000);
  return `${days}d ${hrs}h`;
}

export default function Awards() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [plat, setPlat] = useState("all");
  const [years, setYears] = useState([]);
  const [viewYear, setViewYear] = useState(null); // null = current

  const load = () => {
    const url = viewYear ? `/awards/year/${viewYear}` : "/awards";
    api.get(url).then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [viewYear]); // eslint-disable-line
  useEffect(() => { api.get("/awards/years").then(({ data }) => setYears(data)).catch(() => {}); }, []);

  const vote = async (categoryId, nomineeId) => {
    if (!user) { toast("Sign in to vote"); return; }
    // optimistic: flip the Voted chip instantly, then reconcile with the server
    setData((d) => d && ({ ...d, categories: d.categories.map((c) => c.id === categoryId ? { ...c, my_vote: nomineeId } : c) }));
    try { await api.post("/awards/vote", { category_id: categoryId, nominee_user_id: nomineeId }); toast.success("Vote cast"); load(); }
    catch (e) { toast.error(errText(e)); load(); }
  };

  const cats = useMemo(() => {
    if (!data) return [];
    return plat === "all" ? data.categories : data.categories.filter((c) => c.platform === plat);
  }, [data, plat]);

  if (!data) return <Loading />;
  const yr = data.year;
  const platforms = data.platforms || [{ key: "global", label: "Global" }];
  const cd = yr.phase !== "archived" && countdown(yr.show_date);
  const fanCat = cats.find((c) => c.type === "fan_favorite");

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-gold/15">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,rgba(212,175,55,0.22),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(90deg,#d4af37 0 1px,transparent 1px 60px)" }} />
        <div className="relative px-4 md:px-6 py-12 text-center">
          <div className="inline-flex items-center gap-2 text-gold text-[11px] font-semibold tracking-[0.3em] uppercase mb-3"><Sparkles size={13} /> The Virtual Entertainment Awards</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="virtuawards-title">
            <span className="text-white">VIRTU</span><span className="text-gold-grad">AWARDS</span>
          </h1>
          <div className="mt-2 font-display font-bold text-2xl text-gold">{yr.year}</div>
          <div className="mt-4 flex items-center justify-center gap-3 flex-wrap text-sm">
            <span className="px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold font-medium" data-testid="awards-phase">{PHASE_LABEL[yr.phase] || yr.phase}</span>
            <span className="px-3 py-1.5 rounded-full surface-2 text-muted-foreground inline-flex items-center gap-1.5"><Clock size={13} /> Show: {new Date(yr.show_date + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
            {cd && <span className="px-3 py-1.5 rounded-full surface-2 text-eblue font-medium">in {cd}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-4 max-w-xl mx-auto">From avatar to icon — the metaverse's premier award show honoring the best creators across every virtual world.</p>
        </div>
      </div>

      <div className="px-4 md:px-6 py-6">
        {/* Year switcher */}
        {years.length > 1 && (
          <div className="flex items-center gap-2 mb-5 overflow-x-auto hide-scroll">
            <History size={15} className="text-muted-foreground shrink-0" />
            <button onClick={() => setViewYear(null)} data-testid="awards-year-current" className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${!viewYear ? "bg-gold text-black font-semibold" : "surface-2 text-muted-foreground"}`}>Current</button>
            {years.filter((y) => y.phase === "archived").map((y) => (
              <button key={y.year} onClick={() => setViewYear(y.year)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${viewYear === y.year ? "bg-gold text-black font-semibold" : "surface-2 text-muted-foreground"}`}>{y.year}</button>
            ))}
          </div>
        )}

        {/* Platform filter */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto hide-scroll">
          <button onClick={() => setPlat("all")} data-testid="awards-plat-all" className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${plat === "all" ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>All Categories</button>
          {platforms.map((p) => (
            <button key={p.key} onClick={() => setPlat(p.key)} data-testid={`awards-plat-${p.key}`} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${plat === p.key ? "bg-white text-black" : "surface-2 text-muted-foreground hover:text-white"}`}>
              {p.key === "global" && <Globe size={13} />}{p.label}
            </button>
          ))}
        </div>

        {fanCat && plat === "all" && <FanFavorite cat={fanCat} year={yr} onDone={load} user={user} />}

        <div className="grid md:grid-cols-2 gap-4">
          {cats.filter((c) => c.type !== "fan_favorite").map((c) => (
            <CategoryCard key={c.id} c={c} onVote={vote} canVote={c.can_vote && !!user} />
          ))}
        </div>

        <p className="text-xs text-muted-foreground/60 text-center mt-10 max-w-xl mx-auto">
          VirtuAwards nominees are generated automatically from VirtuStars performance data (VStar Score + engagement), then decided by community voting. Historical results are archived permanently each year.
        </p>
      </div>
    </div>
  );
}

function CategoryCard({ c, onVote, canVote }) {
  const platformTone = c.platform === "global" ? "text-gold" : "text-eblue";
  return (
    <div className="surface rounded-2xl p-5 card-hover" data-testid={`award-cat-${c.id}`}>
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <div className={`text-[10px] font-semibold tracking-widest uppercase ${platformTone} mb-1 flex items-center gap-1`}>{c.platform === "global" ? <Globe size={11} /> : <Star size={11} />}{c.platform_label}</div>
          <h3 className="font-display font-semibold text-lg leading-tight">{c.name}</h3>
          {c.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>}
        </div>
      </div>

      {c.winner && (
        <Link to={`/${c.winner.user.username}`} className="flex items-center gap-3 p-3 rounded-xl bg-gold/10 border border-gold/30 mb-3" data-testid={`award-winner-${c.id}`}>
          <div className="relative"><Avatar src={c.winner.user.avatar_url} name={c.winner.user.display_name} size={48} /><Crown size={16} className="absolute -top-2 left-1/2 -translate-x-1/2 text-gold" /></div>
          <div><div className="text-[10px] uppercase tracking-widest text-gold">🏆 Winner</div><div className="font-semibold"><CreatorName user={c.winner.user} /></div></div>
        </Link>
      )}

      {c.nominees.length === 0 ? (
        <div className="text-xs text-muted-foreground py-3">Nominees will be announced when the qualification round closes.</div>
      ) : (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{c.round === "finalist" ? "Top 5 Finalists" : "Top 10 Nominees"}</div>
          {c.nominees.map((n) => (
            <div key={n.user.id} className={`flex items-center gap-3 p-2 rounded-xl transition-colors ${n.user.id === c.my_vote ? "bg-eblue/10 border border-eblue/30" : "hover:bg-white/5"}`}>
              <span className="w-5 text-center text-xs text-muted-foreground font-mono">{n.rank}</span>
              <Link to={`/${n.user.username}`} className="shrink-0"><Avatar src={n.user.avatar_url} name={n.user.display_name} size={36} /></Link>
              <Link to={`/${n.user.username}`} className="min-w-0 flex-1"><div className="text-sm font-medium truncate"><CreatorName user={n.user} /></div>
                {n.votes != null && <div className="text-[11px] text-muted-foreground">{n.votes} vote{n.votes === 1 ? "" : "s"}</div>}</Link>
              {canVote && (
                n.user.id === c.my_vote
                  ? <span className="text-eblue text-xs font-medium inline-flex items-center gap-1 shrink-0" data-testid={`award-voted-${c.id}`}><Check size={14} /> Voted</span>
                  : <button onClick={() => onVote(c.id, n.user.id)} data-testid={`award-vote-${c.id}-${n.user.id}`} className="shrink-0 btn-gold rounded-full px-3 py-1 text-xs font-semibold inline-flex items-center gap-1"><Vote size={12} /> Vote</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FanFavorite({ cat, year, onDone, user }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get(`/mentions/search?q=${encodeURIComponent(q.replace("@", ""))}`).then(({ data }) => setResults(data)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const nominate = async (uid) => {
    try { const { data } = await api.post("/awards/fan-nominate", { nominee_user_id: uid }); toast.success("Fan Favorite nomination submitted"); setQ(""); setResults([]); setOpen(false); onDone && onDone(); }
    catch (e) { toast.error(errText(e)); }
  };

  const nominationsOpen = year.fan_nominations_open;
  return (
    <div className="relative overflow-hidden rounded-2xl p-5 mb-6 border border-rose-500/25 bg-gradient-to-br from-rose-500/10 to-transparent" data-testid="fan-favorite-card">
      <div className="flex items-center gap-2 text-rose-400 text-[11px] font-semibold tracking-widest uppercase mb-1"><Heart size={13} /> Community Choice</div>
      <h3 className="font-display font-bold text-xl">Fan Favorite</h3>
      <p className="text-sm text-muted-foreground mt-1">{cat.description}</p>
      <div className="mt-3 flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">{cat.fan_nominations ?? 0} nominations</span>
        {cat.winner && <Link to={`/${cat.winner.user.username}`} className="text-gold font-medium inline-flex items-center gap-1"><Crown size={14} /> {cat.winner.user.display_name}</Link>}
      </div>

      {/* Voting pool (if generated) */}
      {cat.nominees.length > 0 && (
        <div className="mt-4 grid sm:grid-cols-2 gap-1.5">
          {cat.nominees.map((n) => (
            <div key={n.user.id} className={`flex items-center gap-2 p-2 rounded-lg ${n.user.id === cat.my_vote ? "bg-eblue/10 border border-eblue/30" : "surface-2"}`}>
              <Link to={`/${n.user.username}`}><Avatar src={n.user.avatar_url} name={n.user.display_name} size={30} /></Link>
              <Link to={`/${n.user.username}`} className="text-sm truncate flex-1"><CreatorName user={n.user} /></Link>
              {cat.can_vote && user && (n.user.id === cat.my_vote
                ? <span className="text-eblue text-xs inline-flex items-center gap-1"><Check size={12} />Voted</span>
                : <button onClick={async () => { try { await api.post("/awards/vote", { category_id: cat.id, nominee_user_id: n.user.id }); toast.success("Vote cast"); onDone && onDone(); } catch (e) { toast.error(errText(e)); } }} className="btn-gold rounded-full px-2.5 py-1 text-xs font-semibold">Vote</button>)}
            </div>
          ))}
        </div>
      )}

      {nominationsOpen && user && (
        <div className="mt-4 relative">
          <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Nominate a creator — type @username" data-testid="fan-nominate-input"
            className="w-full surface-2 border border-white/10 rounded-xl px-3 h-11 text-sm outline-none focus:border-rose-500/40" />
          {open && results.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 surface rounded-xl border border-white/10 overflow-hidden max-h-56 overflow-y-auto">
              {results.map((r) => (
                <button key={r.id} onClick={() => nominate(r.id)} data-testid={`fan-nominate-${r.username}`} className="w-full flex items-center gap-2 p-2.5 hover:bg-white/5 text-left">
                  <Avatar src={r.avatar_url} name={r.display_name} size={30} />
                  <div className="min-w-0"><div className="text-sm truncate">{r.display_name}</div><div className="text-xs text-muted-foreground">@{r.username}</div></div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
